import { useState, useEffect, useRef } from "react";
import s from "./Header.module.scss";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { scroller } from "react-scroll";
import InstagramLogo from "../components/InstagramLogo";
import TiktokLogo from "../components/TiktokLogo";
import RedLogo from "../components/RedLogo";
import Logo from "../Logo/Logo";
import {
  getLocalizedPath,
  stripLocalePrefix,
  useLanguage,
} from "../contexts/LanguageContext";
import { navText } from "../i18n/nav";
import { INSTAGRAM_URL, LINE_URL, TIKTOK_URL } from "../config/socialLinks";
import { trackCtaClick, trackEvent } from "../utils/analytics";

const languages = ["JP", "EN", "中文", "한국어"];

const Header = ({ logoColor }) => {
  const { lang, setLang } = useLanguage();
  const t = navText[lang];
  const localizedPath = (path) => getLocalizedPath(path, lang);
  const homePath = localizedPath("/");
  const toursPath = localizedPath("/tours");
  const galleryPath = localizedPath("/gallery");
  const itineraryPath = localizedPath("/itinerary");
  const oceanGuidePath = localizedPath("/ocean-guide");
  const droneMapPath = localizedPath("/drone-map");
  const faqPath = localizedPath("/faq");
  const blogPath = localizedPath("/blog");
  const contactPath = localizedPath("/contact");
  const webProductionPath = "/web-production";

  const showLineCta = lang !== "EN" && lang !== "中文";

  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const langDropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const closeMenu = () => {
    setIsOpen(false);
    setIsLangOpen(false);
  };

  const trackNavClick = (label, destination) => {
    trackEvent("nav_click", {
      event_category: "navigation",
      event_label: label,
      label,
      destination,
      placement: isMobile ? "mobile_header" : "desktop_header",
    });
  };

  const trackSocialClick = (label, destination) => {
    trackCtaClick({
      label,
      placement: isMobile ? "mobile_header" : "desktop_header",
      destination,
      destinationType: "external_social",
    });
  };

  const scrollToAboutMe = (e) => {
    e.preventDefault();

    closeMenu();

    if (stripLocalePrefix(location.pathname) !== "/") {
      navigate(`${homePath}#about-me`);

      setTimeout(() => {
        scroller.scrollTo("about-me", {
          duration: 800,
          smooth: "easeInOutQuart",
        });
      }, 100);
    } else {
      scroller.scrollTo("about-me", {
        duration: 800,
        smooth: "easeInOutQuart",
      });
    }
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();

    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  useEffect(() => {
    if (!isLangOpen) return;

    const handleClickOutside = (event) => {
      if (!langDropdownRef.current) return;

      if (!langDropdownRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsLangOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isLangOpen]);

  const changeLanguage = (language, placement) => {
    trackEvent("language_change", {
      event_category: "navigation",
      event_label: language,
      language,
      placement,
    });
    setLang(language);
    navigate(
      `${getLocalizedPath(location.pathname, language)}${location.search}${location.hash}`,
      { replace: true },
    );
  };

  return (
    <header className={`${s.root} ${logoColor ? s.darkHeader : ""}`}>
      <div className={s.nav}>
        {isMobile && (
          <>
            <Logo color={logoColor} />

            <div className={s.mobileRight}>
              {!isOpen && (
                <>
                  <div className={s.mobileLangDropdown} ref={langDropdownRef}>
                    <button
                      type="button"
                      className={s.langTrigger}
                      onClick={() => setIsLangOpen((current) => !current)}
                      aria-haspopup="true"
                      aria-expanded={isLangOpen}
                    >
                      <svg className={s.langIcon} viewBox="0 0 24 24">
                        <path d="M4 6h10" />
                        <path d="M4 12h16" />
                        <path d="M4 18h8" />
                        <path d="M14 4c1.6 2.2 2.4 4.8 2.4 8s-.8 5.8-2.4 8" />
                        <path d="M10 4c-1.6 2.2-2.4 4.8-2.4 8s.8 5.8 2.4 8" />
                        <ellipse cx="12" cy="12" rx="9" ry="9" />
                      </svg>

                      {lang}

                      <span>⌄</span>
                    </button>

                    <div
                      className={`${s.langMenu} ${isLangOpen ? s.show : ""}`}
                    >
                      {languages.map((language) => (
                        <button
                          key={language}
                          type="button"
                          className={lang === language ? s.activeLang : ""}
                          onClick={() => {
                            changeLanguage(language, "mobile_header");
                            setIsLangOpen(false);
                          }}
                        >
                          {language}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    className={s.drawer_open}
                    onClick={() => setIsOpen(true)}
                    aria-label="menu"
                    type="button"
                  >
                    <span></span>
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {!isMobile ? (
          <nav className={s.navTablet}>
            <Logo color={logoColor} />

            <ul>
              <li>
                <Link
                  to={homePath}
                  onClick={() => trackNavClick("home", homePath)}
                >
                  {t.home}
                </Link>
              </li>

              <li>
                <Link
                  to={toursPath}
                  onClick={() => trackNavClick("tours", toursPath)}
                >
                  {t.tours}
                </Link>
              </li>

              <li>
                <Link
                  to={galleryPath}
                  onClick={() => trackNavClick("gallery", galleryPath)}
                >
                  {t.gallery}
                </Link>
              </li>

              <li>
                <Link
                  to={itineraryPath}
                  onClick={() => trackNavClick("itinerary", itineraryPath)}
                >
                  {t.itinerary}
                </Link>
              </li>

              <li>
                <Link
                  to={oceanGuidePath}
                  onClick={() => trackNavClick("ocean_guide", oceanGuidePath)}
                >
                  {t.oceanGuide}
                </Link>
              </li>

              <li>
                <Link
                  to={droneMapPath}
                  onClick={() => trackNavClick("drone_map", droneMapPath)}
                >
                  {t.droneMap}
                </Link>
              </li>

              <li>
                <a
                  href={`${homePath}#about-me`}
                  onClick={(event) => {
                    trackNavClick("about_me", `${homePath}#about-me`);
                    scrollToAboutMe(event);
                  }}
                >
                  {t.aboutMe}
                </a>
              </li>

              <li>
                <Link
                  to={faqPath}
                  onClick={() => trackNavClick("faq", faqPath)}
                >
                  {t.faq}
                </Link>
              </li>

              <li>
                <Link
                  to={blogPath}
                  onClick={() => trackNavClick("blog", blogPath)}
                >
                  {t.blog}
                </Link>
              </li>

              <li>
                <Link
                  to={contactPath}
                  onClick={() => trackNavClick("contact", contactPath)}
                >
                  {t.contact}
                </Link>
              </li>

              {lang === "JP" && (
                <li>
                  <Link
                    className={s.webProductionLink}
                    to={webProductionPath}
                    onClick={() =>
                      trackNavClick("web_production", webProductionPath)
                    }
                  >
                    Web制作
                  </Link>
                </li>
              )}

              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackSocialClick("instagram_header_icon", INSTAGRAM_URL)
                  }
                >
                  <InstagramLogo />
                </a>
              </li>

              <li>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackSocialClick("tiktok_header_icon", TIKTOK_URL)
                  }
                >
                  <TiktokLogo />
                </a>
              </li>

              {lang === "中文" && (
                <li>
                  <a
                    href="https://xhslink.com/m/8WR3vDsT2UL"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackSocialClick(
                        "red_header_icon",
                        "https://xhslink.com/m/8WR3vDsT2UL",
                      )
                    }
                  >
                    <RedLogo />
                  </a>
                </li>
              )}

              <li className={s.langItem}>
                <div className={s.langSelect}>
                  {languages.map((language) => (
                    <button
                      key={language}
                      type="button"
                      className={lang === language ? s.activeLang : ""}
                      onClick={() => {
                        changeLanguage(language, "desktop_header");
                      }}
                    >
                      {language}
                    </button>
                  ))}
                </div>
              </li>
            </ul>
          </nav>
        ) : (
          <nav className={`${s.nav_content} ${isOpen ? s.show : ""}`}>
            <div className={s.menuTop}>
              <button
                className={s.closeBtn}
                onClick={() => setIsOpen(false)}
                aria-label="close"
                type="button"
              >
                <span></span>
              </button>
            </div>

            <div className={s.nav_inner}>
              <ul className={s.nav_list}>
                <li>
                  <Link
                    to={homePath}
                    onClick={() => {
                      trackNavClick("home", homePath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>⌂</span>
                    <span>{t.home}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={toursPath}
                    onClick={() => {
                      trackNavClick("tours", toursPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>≋</span>
                    <span>{t.tours}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={galleryPath}
                    onClick={() => {
                      trackNavClick("gallery", galleryPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>▧</span>
                    <span>{t.gallery}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={itineraryPath}
                    onClick={() => {
                      trackNavClick("itinerary", itineraryPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>□</span>
                    <span>{t.itinerary}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={oceanGuidePath}
                    onClick={() => {
                      trackNavClick("ocean_guide", oceanGuidePath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>☊</span>
                    <span>{t.oceanGuide}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={droneMapPath}
                    onClick={() => {
                      trackNavClick("drone_map", droneMapPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>⌖</span>
                    <span>{t.droneMap}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <a
                    href={`${homePath}#about-me`}
                    onClick={(event) => {
                      trackNavClick("about_me", `${homePath}#about-me`);
                      scrollToAboutMe(event);
                    }}
                  >
                    <span className={s.icon}>○</span>
                    <span>{t.aboutMe}</span>
                    <em>›</em>
                  </a>
                </li>

                <li>
                  <Link
                    to={faqPath}
                    onClick={() => {
                      trackNavClick("faq", faqPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>?</span>
                    <span>{t.faq}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={blogPath}
                    onClick={() => {
                      trackNavClick("blog", blogPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>✎</span>
                    <span>{t.blog}</span>
                    <em>›</em>
                  </Link>
                </li>

                <li>
                  <Link
                    to={contactPath}
                    onClick={() => {
                      trackNavClick("contact", contactPath);
                      closeMenu();
                    }}
                  >
                    <span className={s.icon}>✉</span>
                    <span>{t.contact}</span>
                    <em>›</em>
                  </Link>
                </li>

                {lang === "JP" && (
                  <li>
                    <Link
                      className={s.mobileWebProductionLink}
                      to={webProductionPath}
                      onClick={() => {
                        trackNavClick("web_production", webProductionPath);
                        closeMenu();
                      }}
                    >
                      <span className={s.icon}>⌘</span>
                      <span>Web制作依頼はこちら</span>
                      <em>›</em>
                    </Link>
                  </li>
                )}
              </ul>

              <div className={s.follow}>
                <p>{t.followMe}</p>

                <div>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackSocialClick(
                        "instagram_mobile_follow",
                        INSTAGRAM_URL,
                      );
                      closeMenu();
                    }}
                  >
                    <InstagramLogo />
                  </a>

                  <a
                    href={TIKTOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackSocialClick("tiktok_mobile_follow", TIKTOK_URL);
                      closeMenu();
                    }}
                  >
                    <TiktokLogo />
                  </a>

                  {lang === "中文" && (
                    <a
                      href="https://xhslink.com/m/8WR3vDsT2UL"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackSocialClick(
                          "red_mobile_follow",
                          "https://xhslink.com/m/8WR3vDsT2UL",
                        );
                        closeMenu();
                      }}
                    >
                      <RedLogo />
                    </a>
                  )}
                </div>
              </div>

              <div className={s.ctaGroup}>
                {showLineCta && (
                  <div className={s.cta}>
                    <a
                      href={LINE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackCtaClick({
                          label: "line_header_cta",
                          placement: "mobile_header",
                          destination: LINE_URL,
                          destinationType: "external_booking",
                        });
                        closeMenu();
                      }}
                    >
                      <span className={s.lineIcon}>LINE</span>
                      {t.lineCta}
                      <em>→</em>
                    </a>
                  </div>
                )}

                <div className={`${s.cta} ${s.instaCta}`}>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackSocialClick("instagram_header_cta", INSTAGRAM_URL);
                      closeMenu();
                    }}
                  >
                    <span className={s.instaIcon}>
                      <InstagramLogo />
                    </span>
                    {t.instaCta}
                    <em>→</em>
                  </a>
                </div>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
